import { useEffect, useState, useRef, ReactElement } from "react";
import LineChartWrapper from "../../../../../interruptions/dashboard/wrappers/lineChart";
import { useAsyncFetch } from "../../../../../../system/useFetch";
import {
  DashboardModuleType,
  DashboardSchemaType,
  ICell,
  IDashboardResult, ILimitRecord,
  IQueryDashboardResult
} from "../../../../../interruptions/types";
import { IParentSize, ISelectedNode, Modules } from "../../../../../../types";
import { DateTime } from "luxon";
import { ICustomComponentConfig, IDashboardComponent } from "../../../../types";
import { logger } from "../../../../../../system/utils";

const pollInterval = 30000;

const delay = async  (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

interface IProps {
  component: IDashboardComponent<ICustomComponentConfig>;
  parentSize: IParentSize;
  selectedNode: ISelectedNode;
  schemas: Record<DashboardSchemaType, any>;
}

const palette = {
  Temperature: "#1FE822",
  Oxygen: "#E1C914"
};

const onUpdatePalette = () => logger("onUpdatePalette");

const getCell = (field: string): ICell => {
  return {
    key: "data",
    title: "Temperature",
    module: DashboardModuleType.Production,
    chart: "LINE",
    machine: "EAF",
    dateRange: "LAST_2_DAYS",
    crew: null,
    shift: null,
    dimension: "Heat Number",
    groups: [],
    variables: [
      {
        field,
        aggregate: 2,
        color: "#1FE822",
        unit: "",
        decimals: 0
      }
    ],
    top: 0,
    filterBy: "DIMENSION",
    orderAsc: true,
    filters: [],
    style: {},
    sort: {
      orderBy: "DIMENSION",
      orderAsc: true
    },
    custom: {
      adjustYAxis: true,
      query: {
        limit: 10,
        orderAsc: false
      }
    }
  };
};

const HeatVariables = ( { parentSize, selectedNode, component, schemas }: IProps ): ReactElement => {
  const intervalRef = useRef<any>(null);
  const [ getDashboardData ] = useAsyncFetch<IDashboardResult>();
  const [ getLimits ] = useAsyncFetch<Record<number, ILimitRecord>>();
  const [ cell ] = useState<ICell>(getCell(component.config.code ?? ""));
  const [ cellData, setCellData] = useState<IQueryDashboardResult | null>(null);

  useEffect(() => {
    if (!component.config.code || intervalRef.current) {
      return;
    }

    const processRequest = async () => {
      const factoryDay = DateTime.now();

      const request = await getDashboardData({
        module: "dashboard",
        query: "getDashboardData",
        payload: {
          nodeID: selectedNode.nodeID,
          nodeType: selectedNode.type,
          moduleID: Modules.INTERRUPTIONS,
          factoryDay: factoryDay.toFormat("yyyy-MM-dd"),
          cells: [ cell ]
        }
      });

      if (request) {
        const heats = request.data.result.map(r => r["Heat Number"]);
        if (heats.length > 0) {
          await delay(250);

          const limitsRequest = await getLimits({
            module: "eafHeat",
            query: "getHeatSteelLimits",
            payload: {
              heats,
              field: cell.variables[0].field,
            }
          });

          if (limitsRequest && component.config.code) {
            request.data.limits = {
              [component.config.code]: limitsRequest
            };
          }

          setCellData({
            ...request.data,
            result: request.data.result.filter(r => r[component.config.code ?? ""] > 0)
          });
        }
      }
    };

    intervalRef.current = setInterval(processRequest, pollInterval);
    processRequest().then();

    return () => {
      clearInterval(intervalRef.current as any);
      intervalRef.current = null;
    };
  }, [ schemas, cell ]);


  return schemas && cellData
    ? <LineChartWrapper cell={cell} data={cellData} parentSize={parentSize} palette={palette} updatePalette={onUpdatePalette} node={selectedNode} schemas={schemas}/>
    : <></>;
};

export default HeatVariables;